/** Error-code catalogue mirrored from @nacs/shared `api/errors.ts` (clients map codes, never message strings). */
export const ERROR_CODES = [
  "BAD_REQUEST",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "VALIDATION_ERROR",
  "RATE_LIMITED",
  "PLAN_LIMIT_REACHED",
  "DEVICE_OFFLINE",
  "DEVICE_DISABLED",
  "DEVICE_ALREADY_PAIRED",
  "DEVICE_REJECTED",
  "GATEWAY_UNAVAILABLE",
  "PAYMENT_GATEWAY_UNAVAILABLE",
  "RANGE_TOO_WIDE",
  "OTP_INVALID",
  "OTP_EXPIRED",
  "OTP_LOCKED",
  "OTP_RATE_LIMITED",
  "SMS_UNAVAILABLE",
  "INVITE_NOT_FOUND",
  "INVITE_NOT_OPEN",
  "INVITE_EXPIRED",
  "MEMBER_GUARD",
  "ACCOUNT_PROTECTED",
  "ACCOUNT_LAST_MANAGER",
  "ROLE_IN_USE",
  "CARD_IN_USE",
  "PHOTO_INVALID",
  "IMPORT_INVALID",
  "API_KEY_INVALID",
  "TRIAL_EXPIRED",
  "TENANT_SUSPENDED",
  "TENANT_DELETING",
  "IDEMPOTENCY_MISMATCH",
  "IDEMPOTENCY_IN_PROGRESS",
  "OPERATOR_REQUIRED",
  "OPERATOR_ADMIN_REQUIRED",
  // ── Mobile release controls (ADR-0014 addendum 2026-10-12; typo guard 2026-10-16) ──
  "MOBILE_RELEASE_MIN_ABOVE_SEEN",
  // ── Billing (M7's profile and downgrade codes carried over; ADR-0015 retired the mandate's) ──
  "BILLING_PROFILE_REQUIRED",
  "PLAN_DOWNGRADE_BLOCKED",
  "RAZORPAY_SIGNATURE_INVALID",
  // ── M11 catalogue, orders and add-ons (ADR-0015) ──
  "CATALOG_ITEM_UNKNOWN",
  "CATALOG_ITEM_INACTIVE",
  "CATALOG_PRICE_MISSING",
  "CATALOG_TAX_MISSING",
  "ORDER_NOT_FOUND",
  "ORDER_NOT_PAYABLE",
  "ITEM_NOT_HELD",
  "ADDON_REQUIRED",
  "QUANTITY_OUT_OF_RANGE",
  "PERIOD_CHANGE_PENDING",
  "TRIAL_ALREADY_USED",
  "BASE_ITEM_REQUIRED",
  // ── M20 SMS credits (ADR-0024): the three an operator surface can meet ──
  "SMS_ADJUSTMENT_BELOW_ZERO",
  "SMS_WALLET_NOT_OPENED",
  "SMS_TEMPLATE_INACTIVE",
  "SMS_TEMPLATE_UNKNOWN",
  // ── M7 outbound webhooks (ADR-0013) ──
  "WEBHOOK_URL_REFUSED",
  "WEBHOOK_ENDPOINT_LIMIT",
  "WEBHOOK_ENDPOINT_DISABLED",
  "INTERNAL",
] as const;
type ErrorCode = (typeof ERROR_CODES)[number];

/** Client-side codes for failures that never reached the envelope. */
type ClientErrorCode = "NETWORK" | "TIMEOUT" | "CANCELLED" | "UNKNOWN";

/** Only these codes mean "the session is gone"; 5xx and network blips never log anyone out (rules/01 §2). */
export const RELOGIN_REQUIRED_CODES: ReadonlySet<string> = new Set(["UNAUTHORIZED"]);

interface ValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly code: ErrorCode | ClientErrorCode | (string & {});
  readonly status: number | null;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(input: {
    code: string;
    message: string;
    status?: number | null;
    details?: unknown;
    requestId?: string | null;
  }) {
    super(input.message);
    this.name = "ApiError";
    this.code = input.code;
    this.status = input.status ?? null;
    this.details = input.details;
    this.requestId = input.requestId ?? null;
  }

  /** 422 payloads carry `{path, message}[]`; anything else is treated as opaque. */
  get validationIssues(): ValidationIssue[] {
    if (!Array.isArray(this.details)) return [];
    return this.details.filter(
      (d): d is ValidationIssue =>
        typeof d === "object" && d !== null && typeof (d as ValidationIssue).path === "string",
    );
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * The operator token is missing, expired, or the person is not an operator: the me-gate sends them to the
 * login page (M6: the two failures the api used to fold into one 403 are now told apart — a missing, malformed
 * or expired token is 401, and only an account with no operator row still answers 403 OPERATOR_REQUIRED).
 * OPERATOR_ADMIN_REQUIRED is never a session loss: it means a real operator hit an admin-only route (hidden in
 * the UI for anyone but an admin, so this is a race — a demotion mid-session — not a sign-out).
 */
export function isOperatorSessionLost(error: unknown): boolean {
  return (
    isApiError(error) &&
    (error.status === 401 || (error.status === 403 && error.code === "OPERATOR_REQUIRED"))
  );
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Plain-language summary for toasts and failure cards. Technical detail stays in `detail`. */
export function describeError(error: unknown): { title: string; detail: string | null } {
  if (isApiError(error)) {
    switch (error.code) {
      case "NETWORK":
        return { title: "Cannot reach the server", detail: "Check your connection and try again." };
      case "TIMEOUT":
        return { title: "The server took too long", detail: "Try again in a moment." };
      case "UNAUTHORIZED":
        return { title: "Your operator session has ended", detail: "Sign in again to continue." };
      case "OPERATOR_REQUIRED":
        return { title: "This account is not a Nexborg operator", detail: error.message };
      case "OPERATOR_ADMIN_REQUIRED":
        return { title: "Admin only", detail: "This action needs an admin operator, not just any operator." };
      case "FORBIDDEN":
        return { title: "You do not have access to this", detail: error.message };
      case "NOT_FOUND":
        return { title: "Not found", detail: "It may have been removed, or the link is wrong." };
      case "RATE_LIMITED":
        return { title: "Too many requests", detail: "Wait a moment and try again." };
      case "GATEWAY_UNAVAILABLE":
        return {
          title: "The device gateway is not answering",
          detail: "Live presence is unavailable until it is back. Try again in a moment.",
        };
      case "PAYMENT_GATEWAY_UNAVAILABLE":
        return {
          title: "The payment gateway is not answering",
          detail: "No invoice was raised. Try again in a minute.",
        };
      case "SMS_UNAVAILABLE":
        return {
          title: "Could not send the code",
          detail: "The SMS service is not answering. Try again in a minute.",
        };
      case "MOBILE_RELEASE_MIN_ABOVE_SEEN": {
        // The api names the platform and both numbers; saying them back is the explanation. A refusal
        // that lost its details still carries the api's own sentence.
        const d: Record<string, unknown> = isRecord(error.details) ? error.details : {};
        const name = d["platform"] === "ios" ? "iOS" : d["platform"] === "android" ? "Android" : null;
        const asked = d["minBuild"];
        const newest = d["newestSeenBuild"];
        if (!name || typeof asked !== "number" || typeof newest !== "number")
          return {
            title: "That minimum build is higher than any build the server has seen",
            detail: error.message,
          };
        return {
          title: `${name} minimum build ${asked} is higher than any build seen`,
          detail:
            `The newest ${name} build a phone has reported to the server is ${newest}. Check the number for a typo. ` +
            `If the store build really is ${asked}, install it on a phone and sign in once so the server sees it, then save again. ` +
            "Lowering a minimum is always allowed.",
        };
      }
      case "VALIDATION_ERROR":
        return {
          title: "Some of the details are not valid",
          detail: error.validationIssues.map((i) => `${i.path}: ${i.message}`).join("; ") || error.message,
        };
      case "CONFLICT":
        // The server's message is the explanation ("Organisation is already active"); the code is the detail.
        return { title: error.message, detail: null };
      // ── M11 (ADR-0015): the catalogue and the money ──
      case "CATALOG_ITEM_UNKNOWN":
        return {
          title: "No such catalogue item",
          detail: "The code is not one the product implements, so there is nothing to price or sell.",
        };
      case "CATALOG_ITEM_INACTIVE":
        return {
          title: "This item is not on sale",
          detail: "A draft or retired item cannot be trialled or bought. Put it on sale first.",
        };
      case "CATALOG_PRICE_MISSING":
        return {
          title: "This item has no price yet",
          detail: "Publish a price version for it before anything can be sold or renewed at it.",
        };
      case "CATALOG_TAX_MISSING":
        return {
          title: "This item has no SAC code",
          detail: "A live invoice cannot be raised without one. Ask the accountant, then set it here.",
        };
      case "ORDER_NOT_FOUND":
        return {
          title: "No such order",
          detail: "It may belong to another organisation, or the page is showing a stale list.",
        };
      case "ORDER_NOT_PAYABLE":
        // The server's message names the state it is actually in ("this one is reverted").
        return { title: error.message, detail: "Refresh the page to see where the order stands now." };
      case "ITEM_NOT_HELD":
        return {
          title: error.message,
          detail: "Start its trial or add it without payment before setting a price for it.",
        };
      case "ADDON_REQUIRED":
        return { title: error.message, detail: "The organisation does not hold that add-on." };
      case "PLAN_DOWNGRADE_BLOCKED":
        return {
          title: "That is fewer than the organisation is using",
          detail: "Reduce what it has paired or enrolled first; the API names what is in the way.",
        };
      case "QUANTITY_OUT_OF_RANGE":
        // The api names the item and the min/max the catalogue allows; that IS the explanation.
        return { title: error.message, detail: "The catalogue item's own min and max decide this." };
      case "PERIOD_CHANGE_PENDING":
        return {
          title: "A period change is already scheduled",
          detail: "Undo the scheduled change before setting a different one.",
        };
      case "TRIAL_ALREADY_USED":
        return {
          title: "This item has already been trialled",
          detail: "A trial runs once per item. Add it without payment instead, or extend the one it had.",
        };
      case "BASE_ITEM_REQUIRED":
        return {
          title: "The base plan cannot be cancelled",
          detail: "Deleting the organisation is the way out (Danger zone), not removing its base item.",
        };
      // ── M20 (ADR-0024): the wallet and the templates registry ──
      case "SMS_ADJUSTMENT_BELOW_ZERO":
        // The api's message names the balance it would have gone below; the fix is a smaller removal.
        return {
          title: "That would take the balance below zero",
          detail: error.message || "Remove less, or leave the balance as it is.",
        };
      case "SMS_WALLET_NOT_OPENED":
        return {
          title: "This organisation has not opened SMS yet",
          detail:
            "It has no wallet until somebody there opens the SMS screen; there is nothing to change yet.",
        };
      case "SMS_TEMPLATE_INACTIVE":
        return {
          title: error.message || "This template cannot be used right now",
          detail: "A retired template, or one still waiting for its DLT id, is never sent.",
        };
      case "SMS_TEMPLATE_UNKNOWN":
        return {
          title: "No such template",
          detail: "It may have been removed, or the page is showing a stale list.",
        };
      default:
        return {
          title: error.message || "Something went wrong",
          detail:
            [error.code, error.requestId ? `request ${error.requestId}` : null].filter(Boolean).join(" · ") ||
            null,
        };
    }
  }
  if (error instanceof Error) return { title: "Something went wrong", detail: error.message };
  return { title: "Something went wrong", detail: null };
}
